/**
 * Add a shooting day.
 *
 * ONE ROW IS ONE UNIT ON ONE LOCAL DATE. That is the whole shape of this
 * screen, and the reason it asks for the unit before anything else: 15 April
 * with the 1st Unit and 15 April with the 2nd Unit are two separate unit-days
 * with two different crew calls, and the server's unique index is on the
 * PAIR. A repeated date is refused only when the unit repeats too.
 *
 * THERE IS NO UNIT-CREATION ROUTE, AND THIS SCREEN DOES NOT INVENT ONE.
 * `POST /shoot-days` takes `unit_key` and creates the `production_unit` on
 * first use, in the same transaction. So the picker offers the units this
 * production already has, plus naming a new one — which is the only way a
 * unit comes into existence, and is correct, because a unit with no days has
 * never been asked about.
 *
 * OMITTING THE UNIT IS A REAL ANSWER. It resolves to `unspecified` — never to
 * "main" — and that records that nobody was asked rather than guessing. The
 * option says so in those words.
 *
 * WHAT IS TYPED HERE IS AUTHORED, NOT CONFIRMED. A crew call entered on this
 * form is a production value a person wrote down. It writes no
 * `fact_confirmation` row, and a call sheet that later establishes the same
 * time still goes through the confirmation path. Keeping the two provenances
 * distinguishable is the point of the whole evidence chain.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  modeller, type NewShootDay as Body, type Production, type ProductionUnit,
} from '../api';
import { PageHeader } from '../shell/Shell';
import { ErrorState, Loading } from '../shell/states';
import { Note } from '../shell/provenance';
import './newproduction.css';

/** The same rule the server's pattern enforces: `^[a-z0-9][a-z0-9._-]*$`. A
 *  value this cannot produce would be rejected at the boundary, so it is
 *  produced here rather than discovered there. */
function unitKey(name: string): string {
  return name.toLowerCase().trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .replace(/[-._]+$/, '');
}

const NEW_UNIT = '\0new';
const NO_UNIT = '';

export function NewShootDayForm({ units, slug }: {
  units: ProductionUnit[];
  slug: string;
}) {
  const navigate = useNavigate();
  const [choice, setChoice] = useState<string>(units[0]?.unit_key ?? NEW_UNIT);
  const [newUnitName, setNewUnitName] = useState('');
  const [date, setDate] = useState('');
  const [dayNumber, setDayNumber] = useState('');
  const [call, setCall] = useState('');
  const [wrap, setWrap] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const creatingUnit = choice === NEW_UNIT;
  const derivedKey = unitKey(newUnitName);
  const ready = date !== ''
    && (!creatingUnit || derivedKey !== '')
    /* The server refuses a wrap without a call — it cannot become a duration
       without one — so the form does not offer that combination. */
    && (wrap === '' || call !== '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body: Body = {
      local_date: date,
      day_number: dayNumber === '' ? null : Number(dayNumber),
      call_time_local: call || null,
      estimated_wrap_local: wrap || null,
      notes: notes.trim() || null,
    };
    if (creatingUnit) {
      body.unit_key = derivedKey;
      body.unit_display_name = newUnitName.trim();
    } else if (choice !== NO_UNIT) {
      body.unit_key = choice;
    }
    /* NO_UNIT sends neither field and the server resolves 'unspecified'.
       Sending `unit_key: 'unspecified'` explicitly would be the client
       asserting the thing 060 exists to avoid asserting. */
    try {
      await modeller.createShootDay(slug, body);
      /* Back to the detail screen, which refetches on mount — the new row is
         there without a reload, and no local copy of the list can go stale. */
      navigate(`/modeller/productions/${slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="Add a shooting day" count={slug} />
      <form className="pem-form" onSubmit={submit}>
        <label className="pem-field">
          <span className="pem-label">Unit</span>
          <select value={choice} onChange={(e) => setChoice(e.target.value)}>
            {units.map((u) => (
              <option key={u.unit_key} value={u.unit_key}>{u.display_name}</option>
            ))}
            <option value={NEW_UNIT}>Name a new unit</option>
            <option value={NO_UNIT}>Do not specify a unit</option>
          </select>
          <span className="pem-provenance">
            {creatingUnit
              ? 'A unit exists from the first day that names it. There is no separate step.'
              : choice === NO_UNIT
                ? 'Recorded as unspecified: nobody was asked. Not a guess at the main unit.'
                : 'A unit this production already has.'}
          </span>
        </label>

        {creatingUnit && (
          <label className="pem-field">
            <span className="pem-label">Unit name</span>
            <input value={newUnitName} autoFocus
                   onChange={(e) => setNewUnitName(e.target.value)}
                   placeholder="2nd Unit" />
            <span className="pem-provenance">
              This production's own wording. Referred to as{' '}
              <code>{derivedKey || '—'}</code>.
            </span>
          </label>
        )}

        <div className="pem-form__pair">
          <label className="pem-field">
            <span className="pem-label">Local date</span>
            <input type="date" value={date} required
                   onChange={(e) => setDate(e.target.value)} />
            <span className="pem-provenance">
              The date on the ground, in this production's time zone.
            </span>
          </label>
          <label className="pem-field">
            <span className="pem-label">Day number</span>
            <input type="number" min={1} value={dayNumber}
                   onChange={(e) => setDayNumber(e.target.value)}
                   placeholder="if known" />
            <span className="pem-provenance">
              The schedule's own numbering. Leave blank rather than counting.
            </span>
          </label>
        </div>

        <div className="pem-form__pair">
          <label className="pem-field">
            <span className="pem-label">Crew call</span>
            <input type="time" value={call} onChange={(e) => setCall(e.target.value)} />
            <span className="pem-provenance">
              Optional. A call sheet can establish it later instead.
            </span>
          </label>
          <label className="pem-field">
            <span className="pem-label">Estimated wrap</span>
            <input type="time" value={wrap} disabled={call === ''}
                   onChange={(e) => setWrap(e.target.value)} />
            <span className="pem-provenance">
              {call === ''
                ? 'Needs a crew call first: a wrap is only a duration against one.'
                : "The day's length is calculated from these two, not typed."}
            </span>
          </label>
        </div>

        <label className="pem-field">
          <span className="pem-label">Notes</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)}
                 placeholder="optional" />
        </label>

        <div className="pem-form__actions">
          <button type="submit" className="pem-btn" disabled={busy || !ready}>
            {busy ? 'Adding' : 'Add shooting day'}
          </button>
          <button type="button" className="pem-btn pem-btn--quiet"
                  onClick={() => navigate(`/modeller/productions/${slug}`)}>
            Cancel
          </button>
        </div>

        {error && <p className="pem-form__error">{error}</p>}

        <Note>
          Times entered here are written down by you. A call sheet that says
          the same thing is still confirmed separately, so the two never become
          indistinguishable.
        </Note>
      </form>
    </>
  );
}

/**
 * The route component: loads the production so the unit picker offers the
 * units that actually exist, rather than a text box that would let the same
 * unit be created twice under two spellings.
 */
export function NewShootDay() {
  const { slug = '' } = useParams();
  const [state, setState] = useState<
    { t: 'loading' } | { t: 'error'; e: unknown } | { t: 'ok'; p: Production }
  >({ t: 'loading' });

  useEffect(() => {
    let live = true;
    modeller.production(slug)
      .then((p) => { if (live) setState({ t: 'ok', p }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    return () => { live = false; };
  }, [slug]);

  if (state.t === 'loading') {
    return <><PageHeader title="Add a shooting day" /><Loading what={slug} /></>;
  }
  if (state.t === 'error') {
    return <><PageHeader title="Add a shooting day" /><ErrorState what={slug} error={state.e} /></>;
  }
  return <NewShootDayForm slug={slug} units={state.p.units} />;
}
